import { useEffect, useState } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import { useTranslation, parseRichText } from '../i18n';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { LINKS } from '../brand';
import { Layout } from '../components/Layout';
import { Eyebrow, PageBody, SectionHeading, CtaPill, ArrowLink } from '../primitives';

/**
 * Studio download / onboarding page. Ported from the v1 Studio download
 * mockup ("Studio - Download · Gposingway").
 *
 * ──────────────────────────────────────────────────────────────────────────
 * IN DEVELOPMENT - read before wiring real downloads.
 *
 * The Gposingway Studio desktop app (sibling repo github.com/gposingway/studio) is at an
 * early stage (v0.1.0, no published binary releases). This page therefore ships as a
 * *preview*: there are intentionally NO real download links, versions, or file sizes - the
 * prototype's `v8.3.1` / "14.2 MB" / dated release notes were placeholder fiction. The
 * platform cards render a "builds coming soon" state and point to the GitHub repo, and an
 * in-development banner sits at the top of the page.
 *
 * When Studio cuts its first public release, swap the coming-soon states for real artifacts:
 *   - drive version + per-asset URLs/sizes from a release feed (GitHub Releases for
 *     gposingway/studio, or a small backend endpoint - see brand.ts note about /api/stats),
 *   - re-enable the platform download buttons and the releases list,
 *   - and add a `LINKS.studioDownload` (or per-asset) entry to brand.ts rather than inlining.
 *
 * Differences from the prototype, on purpose:
 *   - no light/dark theme toggle (the SPA is dark-only),
 *   - copy lives in i18n (`studio.*` in resources/i18n/en.json) with [[em]] accent markers,
 *   - the fabricated "app window" mockup is omitted (it asserted a shipped v8.3.1 product),
 *   - OS auto-detect + the install-instructions tabs are Preact state, not <html> attributes.
 * ──────────────────────────────────────────────────────────────────────────
 */
export function StudioPage() {
    const t = useTranslation();
    useDocumentTitle(t('studio.pageTitle'));

    return (
        <Layout current="studio">
            <DevBanner />
            <Hero />
            <PlatformCards />
            <HowItWorks />
            <Features />
            <InstallInstructions />
            <Releases />
        </Layout>
    );
}

// ─── in-development banner ───────────────────────────────────────────────────

function DevBanner() {
    const t = useTranslation();
    return (
        <div class="border-b border-accent-soft bg-accent-glow">
            <div class="mx-auto flex max-w-[1680px] flex-wrap items-center gap-x-3 gap-y-1 px-[clamp(20px,3.5vw,56px)] py-3">
                <span class="inline-flex items-center gap-1.5 rounded-full border border-accent-soft px-2.5 py-0.5 font-mono text-[10.5px] uppercase tracking-[0.14em] text-accent">
                    <span class="h-1.5 w-1.5 rounded-full bg-accent" />
                    {t('studio.devBanner.label')}
                </span>
                <p class="text-[13px] text-fg-secondary">
                    {t('studio.devBanner.body')}{' '}
                    <a
                        href={LINKS.github.studio}
                        target="_blank"
                        rel="noreferrer noopener"
                        class="text-accent underline-offset-2 hover:underline"
                    >
                        {t('studio.devBanner.link')}
                    </a>
                    .
                </p>
            </div>
        </div>
    );
}

// ─── hero ────────────────────────────────────────────────────────────────────

function Hero() {
    const t = useTranslation();
    const os = useDetectedOs();
    const titleSegments = parseRichText(t('studio.hero.title'));

    const detected =
        os === 'windows' || os === 'linux'
            ? t('studio.hero.detected', { os: t(`studio.os.${os}`) })
            : t('studio.hero.detectedUnknown');

    return (
        <header class="border-b border-rule">
            <PageBody rhythm="tight">
                <div class="flex flex-wrap items-center gap-2 font-mono text-[11px] uppercase tracking-[0.14em] text-fg-muted">
                    <span>{t('studio.hero.eyebrow1')}</span>
                    <span class="text-fg-faint">·</span>
                    <span class="text-accent">{t('studio.hero.eyebrow2')}</span>
                    <span class="text-fg-faint">·</span>
                    <span>{t('studio.hero.eyebrow3')}</span>
                </div>

                <h1 class="mt-4 max-w-[18ch] font-display text-[clamp(36px,4.6vw,64px)] font-normal leading-[1.05] tracking-tight text-fg-primary">
                    {titleSegments.map((seg, i) =>
                        seg.highlighted
                            ? <em key={i} class="italic text-accent">{seg.text}</em>
                            : <span key={i}>{seg.text}</span>,
                    )}
                </h1>

                <p class="mt-6 max-w-[56ch] font-display text-[clamp(17px,1.4vw,21px)] font-light leading-[1.45] text-fg-primary">
                    {t('studio.hero.lede')}{' '}
                    <span class="text-fg-muted">{t('studio.hero.ledeMuted')}</span>
                </p>

                <div class="mt-6 inline-flex items-center gap-2 rounded-full border border-rule px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.12em] text-fg-muted">
                    <span class="h-1.5 w-1.5 rounded-full bg-fg-faint" />
                    {detected}
                </div>
            </PageBody>
        </header>
    );
}

// ─── platform cards ───────────────────────────────────────────────────────────

function PlatformCards() {
    const t = useTranslation();
    const os = useDetectedOs();

    return (
        <section class="border-b border-rule bg-surface-raised">
            <PageBody rhythm="section">
                <div class="grid grid-cols-1 gap-5 md:grid-cols-2">
                    <PlatformCard
                        icon={<WindowsIcon />}
                        name={t('studio.platforms.windows.name')}
                        sub={t('studio.platforms.windows.sub')}
                        primaryFile={t('studio.platforms.windows.primary')}
                        planned={t('studio.platforms.windows.planned')}
                        recommended={os === 'windows'}
                    />
                    <PlatformCard
                        icon={<LinuxIcon />}
                        name={t('studio.platforms.linux.name')}
                        sub={t('studio.platforms.linux.sub')}
                        primaryFile={t('studio.platforms.linux.primary')}
                        planned={t('studio.platforms.linux.planned')}
                        recommended={os === 'linux'}
                    />
                </div>
            </PageBody>
        </section>
    );
}

function PlatformCard({
    icon,
    name,
    sub,
    primaryFile,
    planned,
    recommended,
}: {
    icon: ComponentChildren;
    name: string;
    sub: string;
    primaryFile: string;
    planned: string;
    recommended?: boolean;
}) {
    const t = useTranslation();
    return (
        <article
            class={`flex flex-col rounded-md border bg-surface-base p-6 transition-colors ${
                recommended ? 'border-accent shadow-[0_0_0_1px_var(--color-accent-soft)]' : 'border-rule'
            }`}
        >
            <div class="flex items-center gap-3">
                <span class="grid h-10 w-10 place-items-center rounded-md border border-rule text-fg-primary">
                    {icon}
                </span>
                <div>
                    <div class="font-display text-[22px] italic font-normal leading-tight text-fg-primary">{name}</div>
                    <div class="font-mono text-[11px] uppercase tracking-[0.12em] text-fg-muted">{sub}</div>
                </div>
                {recommended && (
                    <span class="ml-auto rounded-full border border-accent bg-accent-glow px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.1em] text-accent">
                        {t('studio.platforms.recommended')}
                    </span>
                )}
            </div>

            {/* Coming-soon state: no real artifact to download yet (see file header). */}
            <div class="mt-5 flex items-center justify-between gap-3 rounded-md border border-dashed border-rule-strong px-4 py-3">
                <span class="flex flex-col">
                    <span class="font-mono text-[13px] text-fg-secondary">{primaryFile}</span>
                    <span class="font-mono text-[11px] uppercase tracking-[0.1em] text-fg-faint">
                        {t('studio.platforms.comingSoon')}
                    </span>
                </span>
                <span aria-hidden="true" class="font-mono text-fg-faint">↓</span>
            </div>

            <p class="mt-3 text-[13px] text-fg-muted">{planned}</p>

            <div class="mt-5 border-t border-rule pt-4">
                <ArrowLink href={LINKS.github.studio} variant="primary" external>
                    {t('studio.platforms.watchGithub')}
                </ArrowLink>
            </div>
        </article>
    );
}

// ─── how it works ──────────────────────────────────────────────────────────────

function HowItWorks() {
    const t = useTranslation();
    const steps = [1, 2, 3, 4] as const;
    return (
        <section class="border-b border-rule">
            <PageBody rhythm="section">
                <div class="mb-8 flex flex-wrap items-end justify-between gap-4">
                    <SectionHeading
                        eyebrow={t('studio.how.eyebrow')}
                        title={t('studio.how.title')}
                        size="md"
                        as="h2"
                    />
                    <Eyebrow>{t('studio.how.aside')}</Eyebrow>
                </div>

                <div class="grid grid-cols-1 gap-px overflow-hidden rounded-md border border-rule bg-rule sm:grid-cols-2 lg:grid-cols-4">
                    {steps.map((n) => (
                        <div key={n} class="flex flex-col gap-2 bg-surface-raised px-5 py-6">
                            <Eyebrow tone="faint">{t(`studio.how.step${n}num`)}</Eyebrow>
                            <Highlight
                                value={t(`studio.how.step${n}title`)}
                                class="font-display text-[19px] italic font-normal leading-snug text-fg-primary"
                            />
                            <p class="text-[13px] leading-relaxed text-fg-muted">{t(`studio.how.step${n}body`)}</p>
                        </div>
                    ))}
                </div>
            </PageBody>
        </section>
    );
}

// ─── features ──────────────────────────────────────────────────────────────────

function Features() {
    const t = useTranslation();
    const items = [1, 2, 3] as const;
    return (
        <section class="border-b border-rule bg-surface-raised">
            <PageBody rhythm="section">
                <div class="mb-8 flex flex-wrap items-end justify-between gap-4">
                    <SectionHeading
                        eyebrow={t('studio.features.eyebrow')}
                        title={t('studio.features.title')}
                        size="md"
                        as="h2"
                    />
                    <Eyebrow>{t('studio.features.aside')}</Eyebrow>
                </div>

                <div class="grid grid-cols-1 gap-5 md:grid-cols-3">
                    {items.map((n) => (
                        <article key={n} class="flex flex-col gap-3 rounded-md border border-rule bg-surface-base p-6">
                            <Eyebrow>{t(`studio.features.f${n}num`)}</Eyebrow>
                            <Highlight
                                value={t(`studio.features.f${n}title`)}
                                class="font-display text-[22px] italic font-normal leading-tight text-fg-primary"
                            />
                            <p class="text-sm leading-relaxed text-fg-muted">{t(`studio.features.f${n}body`)}</p>
                        </article>
                    ))}
                </div>
            </PageBody>
        </section>
    );
}

// ─── install instructions ───────────────────────────────────────────────────────

type Platform = 'windows' | 'linux';

const INSTALL_TABS: Platform[] = ['windows', 'linux'];

function InstallInstructions() {
    const t = useTranslation();
    const detected = useDetectedOs();
    const [tab, setTab] = useState<Platform>('windows');

    // Default the tab to the visitor's OS once detected (without fighting later manual clicks:
    // the effect only runs when detection resolves).
    useEffect(() => {
        if (detected === 'windows' || detected === 'linux') setTab(detected);
    }, [detected]);

    const steps = [1, 2, 3, 4] as const;

    // Roving ArrowLeft/ArrowRight between tabs, per the WAI-ARIA tabs pattern.
    const onTablistKeyDown = (e: KeyboardEvent) => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        e.preventDefault();
        const idx = INSTALL_TABS.indexOf(tab);
        const delta = e.key === 'ArrowRight' ? 1 : INSTALL_TABS.length - 1;
        const next = INSTALL_TABS[(idx + delta) % INSTALL_TABS.length];
        if (next) {
            setTab(next);
            document.getElementById(`studio-tab-${next}`)?.focus();
        }
    };

    return (
        <section class="border-b border-rule">
            <PageBody rhythm="section">
                <div class="mb-6">
                    <SectionHeading
                        eyebrow={t('studio.install.eyebrow')}
                        title={t('studio.install.title')}
                        size="md"
                        as="h2"
                    />
                    <p class="mt-3 max-w-[60ch] text-[13px] text-fg-muted">{t('studio.install.note')}</p>
                </div>

                <div
                    role="tablist"
                    aria-label={t('studio.install.eyebrow')}
                    onKeyDown={onTablistKeyDown}
                    class="mb-6 flex gap-2"
                >
                    <TabButton platform="windows" active={tab === 'windows'} onClick={() => setTab('windows')}>
                        <WindowsIcon size={14} /> {t('studio.install.tabWindows')}
                    </TabButton>
                    <TabButton platform="linux" active={tab === 'linux'} onClick={() => setTab('linux')}>
                        <LinuxIcon size={14} /> {t('studio.install.tabLinux')}
                    </TabButton>
                </div>

                {/* Only the active panel is rendered; its id + aria-labelledby track the active tab. */}
                <div
                    role="tabpanel"
                    id={`studio-panel-${tab}`}
                    aria-labelledby={`studio-tab-${tab}`}
                    tabIndex={0}
                    class="outline-none"
                >
                    <ol class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        {steps.map((n) => (
                            <li key={n} class="flex gap-4 rounded-md border border-rule bg-surface-raised p-5">
                                <span class="font-mono text-[13px] text-fg-faint">{String(n).padStart(2, '0')}</span>
                                <div>
                                    <h4 class="font-sans text-[15px] font-medium text-fg-primary">
                                        {t(`studio.install.${tab}.s${n}title`)}
                                    </h4>
                                    <p class="mt-1 text-[13px] leading-relaxed text-fg-muted">
                                        {t(`studio.install.${tab}.s${n}body`)}
                                    </p>
                                </div>
                            </li>
                        ))}
                    </ol>

                    <div class="mt-5 rounded-md border border-dashed border-rule-strong bg-surface-raised px-5 py-4 text-[13px] leading-relaxed text-fg-muted">
                        {t(`studio.install.${tab}Callout`)}
                    </div>
                </div>
            </PageBody>
        </section>
    );
}

function TabButton({
    platform,
    active,
    onClick,
    children,
}: {
    platform: Platform;
    active: boolean;
    onClick: () => void;
    children: ComponentChildren;
}) {
    return (
        <button
            type="button"
            role="tab"
            id={`studio-tab-${platform}`}
            aria-selected={active}
            aria-controls={`studio-panel-${platform}`}
            tabIndex={active ? 0 : -1}
            onClick={onClick}
            class={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-[13px] transition-colors ${
                active
                    ? 'border-accent bg-accent-glow text-accent'
                    : 'border-rule text-fg-muted hover:border-accent-soft hover:text-fg-primary'
            }`}
        >
            {children}
        </button>
    );
}

// ─── releases ──────────────────────────────────────────────────────────────────

function Releases() {
    const t = useTranslation();
    return (
        <section class="border-b border-rule bg-surface-raised">
            <PageBody rhythm="section">
                <SectionHeading
                    eyebrow={t('studio.releases.eyebrow')}
                    title={t('studio.releases.title')}
                    size="md"
                    as="h2"
                />
                <p class="mt-4 max-w-[60ch] text-sm leading-relaxed text-fg-muted">
                    {t('studio.releases.body')}
                </p>
                <div class="mt-6">
                    <CtaPill href={LINKS.github.installGuide} external>
                        {t('studio.releases.cta')} <span aria-hidden="true">→</span>
                    </CtaPill>
                </div>
            </PageBody>
        </section>
    );
}

// ─── shared bits ────────────────────────────────────────────────────────────────

/** Inline renderer for translated strings carrying `[[em]]…[[/em]]` accent markers. */
function Highlight({ value, class: extraClass }: { value: string; class?: string }) {
    const segments = parseRichText(value);
    return (
        <p class={extraClass}>
            {segments.map((seg, i) =>
                seg.highlighted
                    ? <em key={i} class="italic text-accent">{seg.text}</em>
                    : <span key={i}>{seg.text}</span>,
            )}
        </p>
    );
}

type DetectedOs = 'windows' | 'linux' | 'mac' | 'other' | 'unknown';

/**
 * Best-effort client OS sniff to highlight the matching platform card + default the install tab.
 * Cosmetic only - never gates content. Starts 'unknown' (matches SSR/first paint) and resolves
 * after mount so hydration stays stable.
 */
function useDetectedOs(): DetectedOs {
    const [os, setOs] = useState<DetectedOs>('unknown');
    useEffect(() => {
        if (typeof navigator === 'undefined') return;
        const ua = (navigator.userAgent || '').toLowerCase();
        if (ua.includes('win')) setOs('windows');
        else if (ua.includes('linux') && !ua.includes('android')) setOs('linux');
        else if (ua.includes('mac')) setOs('mac');
        else setOs('other');
    }, []);
    return os;
}

function WindowsIcon({ size = 20 }: { size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M3 4l8-1.1V11H3V4zm9-1.2L21 1.5V11h-9V2.8zM3 12h8v7.1L3 18V12zm9 0h9v9.5l-9-1.2V12z" />
        </svg>
    );
}

function LinuxIcon({ size = 20 }: { size?: number }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 2c-2.2 0-4 1.8-4 4v3.2c0 1-.4 2-1.1 2.7-1.7 1.7-2.4 4-2.1 6.2.2 1.4 1 2.7 2.2 3.3 1 .5 2 .6 3 .6h4c1 0 2-.1 3-.6 1.2-.6 2-1.9 2.2-3.3.3-2.2-.4-4.5-2.1-6.2-.7-.7-1.1-1.7-1.1-2.7V6c0-2.2-1.8-4-4-4zm-1.5 5c.6 0 1 .4 1 1s-.4 1-1 1-1-.4-1-1 .4-1 1-1zm3 0c.6 0 1 .4 1 1s-.4 1-1 1-1-.4-1-1 .4-1 1-1zm-1.5 3.6c.8 0 1.6.3 2.1.8.3.3.1.7-.3.7h-3.6c-.4 0-.6-.4-.3-.7.5-.5 1.3-.8 2.1-.8z" />
        </svg>
    );
}
